import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Upload, CheckCircle2, AlertCircle } from 'lucide-react';

/**
 * File Upload Component
 * Supports drag & drop and file picker for JSON and CDM files
 */
const FileUpload = ({ onFileUpload, isLoading }) => {
  const { t } = useTranslation();
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);

  const validateFile = (file) => {
    // Check file type
    if (!file.name.endsWith('.json') && !file.name.endsWith('.cdm')) {
      setError(t('fileUpload.onlyJsonCdm'));
      return false;
    }

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setError(t('fileUpload.fileSizeExceeded'));
      return false;
    }

    setError(null);
    return true;
  };

  // Optional: Pre-validate .cdm files on frontend for better UX
  const preValidateCdmFile = async (file) => {
    try {
      // Dynamically import JSZip
      const JSZip = (await import('jszip')).default;
      const zip = await JSZip.loadAsync(file);
      
      // Find JSON files
      const jsonFiles = [];
      zip.forEach((relativePath, entry) => {
        if (!entry.dir && /\.json$/i.test(entry.name)) {
          jsonFiles.push(entry.name);
        }
      });
      
      if (jsonFiles.length === 0) {
        setError('Invalid .cdm file: No JSON file found in archive. Expected "source.json" or "course.json".');
        return false;
      }
      
      // Try to find source.json or course.json
      const hasSourceJson = jsonFiles.some(f => f.toLowerCase().endsWith('source.json'));
      const hasCourseJson = jsonFiles.some(f => f.toLowerCase().endsWith('course.json'));
      
      if (!hasSourceJson && !hasCourseJson && jsonFiles.length > 0) {
        // Has JSON but not the expected names - still valid, just warn
        console.warn(`CDM file contains JSON files but not "source.json" or "course.json": ${jsonFiles.join(', ')}`);
      }
      
      // Try to parse the first JSON to validate structure
      const candidateFile = jsonFiles.find(f => f.toLowerCase().endsWith('source.json')) ||
                           jsonFiles.find(f => f.toLowerCase().endsWith('course.json')) ||
                           jsonFiles[0];
      
      try {
        const jsonText = await zip.file(candidateFile).async('text');
        const jsonData = JSON.parse(jsonText);
        
        // Basic validation - check if it has CDM structure or standard structure
        const hasCdmStructure = jsonData.data && 
                                (jsonData.data.LessonInfo || jsonData.data.Conceptual);
        const hasStandardStructure = jsonData.CourseInfo && jsonData.Topics;
        
        if (!hasCdmStructure && !hasStandardStructure) {
          setError('Invalid .cdm file: The JSON file does not contain a valid course structure. Expected CourseInfo/Topics or data.LessonInfo/data.Conceptual.');
          return false;
        }
        
        console.log('CDM file pre-validation passed:', {
          jsonFile: candidateFile,
          hasCdmStructure,
          hasStandardStructure
        });
        
        return true;
      } catch (jsonError) {
        setError(`Invalid .cdm file: The JSON file "${candidateFile}" is not valid JSON: ${jsonError.message}`);
        return false;
      }
    } catch (error) {
      if (error.message && error.message.includes('Invalid')) {
        // Already set error message
        return false;
      }
      console.warn('CDM pre-validation failed, but proceeding with backend validation:', error);
      // Don't block upload - let backend handle validation
      return true;
    }
  };

  const handleFile = async (file) => {
    console.log('FileUpload: handleFile called with:', file);
    
    if (!validateFile(file)) {
      console.log('FileUpload: validation failed');
      return;
    }
    
    // Optional: Pre-validate .cdm files on frontend
    if (file.name.endsWith('.cdm')) {
      const isValid = await preValidateCdmFile(file);
      if (!isValid) {
        console.log('FileUpload: CDM pre-validation failed');
        return;
      }
    }
    
    console.log('FileUpload: validation passed, calling onFileUpload');
    onFileUpload(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div
        className={`relative border-2 border-dashed rounded-lg p-16 text-center transition-all ${
          dragActive
            ? 'border-teal-500 bg-teal-50'
            : error
            ? 'border-red-300 bg-red-50'
            : 'border-gray-300 bg-white hover:border-teal-400'
        } ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="file-upload"
          ref={(input) => {
            if (input) {
              window.fileInputRef = input;
            }
          }}
          className="hidden"
          onChange={handleChange}
          accept=".json,.cdm"
          disabled={isLoading}
        />

        <div className="cursor-pointer flex flex-col items-center">
          <Upload
            className={`w-20 h-20 mb-6 ${
              dragActive ? 'text-teal-500' : error ? 'text-red-500' : 'text-gray-400'
            }`}
          />

          <h3 className="text-2xl font-semibold text-gray-700 mb-3">
            {dragActive ? t('fileUpload.dropFileHere') : t('fileUpload.uploadCourseFile')}
          </h3>

          <p className="text-gray-500 mb-6 text-lg">
            {t('fileUpload.dragAndDrop')}
          </p>

          <button
            type="button"
            onClick={() => document.getElementById('file-upload').click()}
            className="px-8 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-lg font-medium hover:from-orange-600 hover:to-pink-600 transition-all shadow-md text-lg"
            disabled={isLoading}
          >
            {isLoading ? t('fileUpload.uploading') : t('fileUpload.chooseFile')}
          </button>

          <p className="text-sm text-gray-400 mt-6">
            {t('fileUpload.supportedFormats')}
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">{t('fileUpload.uploadError')}</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;


